import { useMemo } from "react";
import { Klydo } from "../../../api/klydo";
import useGetKlydosByIds from "./useGetKlydosByIds";

export type DeviceFavoriteKlydo = Klydo & {
  klydoId: string;
  /** 1-based position in the device's `favorites` array = playback order. */
  position: number;
};

/**
 * Loads a device's favorite klydos IN THE ORDER THEY ARE STORED.
 *
 * The order of the `favorites` array is the order the clock plays them in
 * (KlydosArray reorders the Room rows back to the id list), while the klydos
 * API returns `find({ id: { $in: ids } })` in Mongo's own order — so the
 * fetched rows must be re-sorted against the id list or the table shows an
 * order the clock does not use.
 *
 * `missingIds` are favorites the API did not return (hard-deleted klydos, for
 * example). They still occupy a position on the clock, so they are reported
 * rather than silently dropped.
 */
const useGetDeviceFavoriteKlydos = (klydoIds: string[] = []) => {
  const ids = Array.isArray(klydoIds)
    ? klydoIds
    : ((klydoIds as { value?: string[] })?.value ?? []);

  const { klydos, isLoading, error } = useGetKlydosByIds(ids);

  const { orderedKlydos, missingIds } = useMemo(() => {
    const klydoById = new Map(klydos.map((klydo) => [klydo.id, klydo]));
    const ordered: DeviceFavoriteKlydo[] = [];
    const missing: string[] = [];

    ids.forEach((id, index) => {
      const klydo = klydoById.get(id);
      if (!klydo) {
        missing.push(id);
        return;
      }
      ordered.push({ ...klydo, klydoId: klydo.id, position: index + 1 });
    });

    return { orderedKlydos: ordered, missingIds: missing };
  }, [klydos, ids]);

  return {
    klydos: orderedKlydos,
    missingIds,
    isLoading,
    hasErrors: !!error,
  };
};

export default useGetDeviceFavoriteKlydos;
